// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { ALPHABET } from "./constants.js";
export const base32 = {
    /**
    * Convert a base32 string to a Uint8Array of bytes.
    *
    * @param {string} base32 - String to convert
    * @returns {Uint8Array} Byte array representation of the input string
    */
    toBytes(base32) {
        const leftover = (base32.length * 5) % 8;
        const offset = leftover === 0
            ? 0
            : 8 - leftover;
        let bits = 0;
        let value = 0;
        let index = 0;
        let output = new Uint8Array(Math.ceil((base32.length * 5) / 8));
        for (let i = 0; i < base32.length; i++) {
            value = (value << 5) | ALPHABET.indexOf(base32[i]);
            bits += 5;
            if (bits >= 8) {
                output[index++] = (value >>> (bits + offset - 8)) & 255;
                bits -= 8;
            }
        }
        if (bits > 0) {
            output[index++] = (value << (bits + offset - 8)) & 255;
        }
        if (leftover !== 0) {
            output = output.slice(1);
        }
        return output;
    }
};
export const bin = {
    /**
    * Convert a binary string to a Uint8Array of bytes.
    *
    * @param {string} bin - String to convert
    * @returns {Uint8Array} Byte array representation of the input string
    */
    toBytes(bin) {
        const bytes = [];
        while (bin.length > 0) {
            const bits = bin.substring(0, 8);
            bytes.push(parseInt(bits, 2));
            bin = bin.substring(8);
        }
        return new Uint8Array(bytes);
    },
    /**
    * Convert a binary string to a hexadecimal representation
    *
    * @param {string} bin - String to convert
    * @returns {string} Hexadecimal representation of the input string
    */
    toHex(bin) {
        return parseInt(bin, 2).toString(16);
    }
};
export const buffer = {
    /**
    * Converts an ArrayBuffer to a base32 string.
    *
    * @param {ArrayBuffer} buffer - Buffer to convert
    * @returns {string} Base32 string representation of the input buffer
    */
    toBase32(buffer) {
        return bytes.toBase32(new Uint8Array(buffer));
    },
    /**
    * Converts an ArrayBuffer to a binary string.
    *
    * @param {ArrayBuffer} buffer - Buffer to convert
    * @returns {string} Binary string representation of the input buffer
    */
    toBin(buffer) {
        return bytes.toBin(new Uint8Array(buffer));
    },
    /**
    * Sums an ArrayBuffer to a decimal integer. If the result is larger than
    * Number.MAX_SAFE_INTEGER, it will be returned as a bigint.
    *
    * @param {ArrayBuffer} buffer - Buffer to convert
    * @returns {bigint|number} Decimal sum of the literal buffer values
    */
    toDec(buffer) {
        return bytes.toDec(new Uint8Array(buffer));
    },
    /**
    * Converts an ArrayBuffer to a hexadecimal string.
    *
    * @param {ArrayBuffer} buffer - Buffer to convert
    * @returns {string} Hexadecimal string representation of the input buffer
    */
    toHex(buffer) {
        return bytes.toHex(new Uint8Array(buffer));
    },
    /**
    * Converts an ArrayBuffer to a UTF-8 text string.
    *
    * @param {ArrayBuffer} buffer - Buffer to convert
    * @returns {string} UTF-8 encoded text string
    */
    toUtf8(buffer) {
        return bytes.toUtf8(new Uint8Array(buffer));
    }
};
export const bytes = {
    /**
    * Converts a Uint8Aarray of bytes to a base32 string.
    *
    * @param {Uint8Array} bytes - Byte array to convert
    * @returns {string} Base32 string representation of the input bytes
    */
    toBase32(bytes) {
        const leftover = (bytes.length * 8) % 5;
        const offset = leftover === 0
            ? 0
            : 5 - leftover;
        let value = 0;
        let output = '';
        let bits = 0;
        for (let i = 0; i < bytes.length; i++) {
            value = (value << 8) | bytes[i];
            bits += 8;
            while (bits >= 5) {
                output += ALPHABET[(value >>> (bits + offset - 5)) & 31];
                bits -= 5;
            }
        }
        if (bits > 0) {
            output += ALPHABET[(value << (5 - (bits + offset))) & 31];
        }
        return output;
    },
    /**
    * Convert a Uint8Array of bytes to a binary string.
    *
    * @param {Uint8Array} bytes - Byte array to convert
    * @returns {string} Binary string representation of the input value
    */
    toBin(bytes) {
        return [...bytes].map(b => b.toString(2).padStart(8, '0')).join('');
    },
    /**
    * Sums an array of bytes to a decimal integer. If the result is larger than
    * Number.MAX_SAFE_INTEGER, it will be returned as a bigint.
    *
    * @param {Uint8Array} bytes - Byte array to convert
    * @returns {bigint|number} Decimal sum of the literal byte values
    */
    toDec(bytes) {
        const integers = [];
        bytes.reverse().forEach(b => integers.push(BigInt(b)));
        const decimal = integers.reduce((sum, byte, index) => {
            return sum + (byte << BigInt(index * 8));
        });
        if (decimal > 9007199254740991n) {
            return decimal;
        }
        else {
            return Number(decimal);
        }
    },
    /**
    * Converts a Uint8Array of bytes to a hexadecimal string.
    *
    * @param {Uint8Array} bytes - Byte array to convert
    * @returns {string} Hexadecimal string representation of the input bytes
    */
    toHex(bytes) {
        const byteArray = [...bytes].map(byte => byte.toString(16).padStart(2, '0'));
        return byteArray.join('').toUpperCase();
    },
    /**
    * Converts a Uint8Array of bytes to a UTF-8 text string.
    *
    * @param {Uint8Array} bytes - Byte array to convert
    * @returns {string} UTF-8 encoded text string
    */
    toUtf8(bytes) {
        return new TextDecoder().decode(bytes);
    }
};
export const dec = {
    /**
    * Convert a decimal integer to a binary string.
    *
    * @param {bigint|number|string} decimal - Integer to convert
    * @param {number} [padding=0] - Minimum length of the resulting string which will be padded as necessary with starting zeroes
    * @returns {string} Binary string representation of the input decimal
    */
    toBin(decimal, padding = 0) {
        if (typeof padding !== 'number') {
            throw new TypeError('Invalid padding');
        }
        try {
            return BigInt(decimal)
                .toString(2)
                .padStart(padding, '0');
        }
        catch (err) {
            throw new RangeError('Invalid decimal integer');
        }
    },
    /**
    * Convert a decimal integer to a Uint8Array of bytes. Fractional part is truncated.
    *
    * @param {bigint|number|string} decimal - Integer to convert
    * @param {number} [padding=0] - Minimum length of the resulting array which will be padded as necessary with starting 0x00 bytes
    * @returns {Uint8Array} Byte array representation of the input decimal
    */
    toBytes(decimal, padding = 0) {
        if (typeof padding !== 'number') {
            throw new TypeError('Invalid padding');
        }
        let integer = BigInt(decimal);
        const bytes = [];
        while (integer > 0) {
            const lsb = BigInt.asUintN(8, integer);
            bytes.push(Number(lsb));
            integer >>= 8n;
        }
        const result = new Uint8Array(Math.max(padding, bytes.length));
        result.set(bytes);
        return (result.reverse());
    },
    /**
    * Convert a decimal integer to a hexadecimal string.
    *
    * @param {(bigint|number|string)} decimal - Integer to convert
    * @param {number} [padding=0] - Minimum length of the resulting string which will be padded as necessary with starting zeroes
    * @returns {string} Hexadecimal string representation of the input decimal
    */
    toHex(decimal, padding = 0) {
        if (typeof padding !== 'number') {
            throw new TypeError('Invalid padding');
        }
        try {
            return BigInt(decimal)
                .toString(16)
                .padStart(padding, '0')
                .toUpperCase();
        }
        catch (err) {
            throw new RangeError('Invalid decimal integer');
        }
    }
};
export const hex = {
    /**
    * Convert a hexadecimal string to a binary string.
    *
    * @param {string} hex - Hexadecimal number string to convert
    * @returns {string} Binary string representation of the input value
    */
    toBin(hex) {
        return [...hex].map(c => dec.toBin(parseInt(c, 16), 4)).join('');
    },
    /**
    * Convert a hexadecimal string to a Uint8Array of bytes.
    *
    * @param {string} hex - Hexadecimal number string to convert
    * @param {number} [padding=0] - Minimum length of the resulting array which will be padded as necessary with starting 0x00 bytes
    * @returns {Uint8Array} Byte array representation of the input value
    */
    toBytes(hex, padding = 0) {
        if (typeof padding !== 'number') {
            throw new TypeError('Invalid padding when converting hex to bytes');
        }
        const hexArray = hex.match(/.{1,2}/g);
        if (!/^[0-9a-f]+$/i.test(hex) || hexArray == null) {
            console.warn('Invalid hex string when converting to bytes');
            return new Uint8Array();
        }
        else {
            const bytes = Uint8Array.from(hexArray.map(byte => parseInt(byte, 16)));
            const result = new Uint8Array(Math.max(padding, bytes.length));
            result.set(bytes.reverse());
            return result.reverse();
        }
    }
};
export const utf8 = {
    /**
    * Convert a UTF-8 text string to a Uint8Array of bytes.
    *
    * @param {string} utf8 - String to convert
    * @returns {Uint8Array} Byte array representation of the input string
    */
    toBytes(utf8) {
        return new TextEncoder().encode(utf8);
    },
    /**
    * Convert a string to a hexadecimal representation
    *
    * @param {string} utf8 - String to convert
    * @returns {string} Hexadecimal representation of the input string
    */
    toHex(utf8) {
        return bytes.toHex(this.toBytes(utf8));
    }
};
export default { base32, bin, bytes, dec, hex, utf8 };
